import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete04Icon, Edit02Icon } from '@hugeicons-pro/core-bulk-rounded'
import { UnfoldLessIcon, UnfoldMoreIcon } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, Checkbox, FormControlLabel, IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useDeleteOrderItemsMutation, useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useGetOrderQuery, useUpdateOrderMutation } from '@/app/services/orders'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxDialog from '@/common/components/TbxDialog/TbxDialog'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'
import { ORDER_STATUS_ARRAY } from '@/common/utils'

import {
    clearCollapsedItems,
    clearExpandedItems,
    clearSelectedItems,
    selectFilteredItemsIds,
    selectFilterOptions,
    selectSelectedItems,
    setCollapsedItemsIds,
    setSelectedItemsIds,
    setSelectedOrderStatus,
} from '../../../../app/slices/orderItemsSlice'
import OrderItemsBulkEdit from '../BulkEdit/OrderItemsBulkEdit'

import OrderItemListFilter from './OrderItemListFilter'
import OrderItemListSort from './OrderItemListSort'

const OrderItemListActions = () => {
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { showMutationError } = useMutationErrorSnackbar()
    const { t } = useTranslation()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const filterOption = useSelector(selectFilterOptions)
    const orderItemsIds = useSelector(selectFilteredItemsIds)

    const selectedItemsArray = useSelector(selectSelectedItems)
    const { data: selectedOrder } = useGetOrderQuery({ organisationId, orderId })
    const { data: orderItems } = useGetOrderItemsQuery({ organisationId, orderId })

    const [deleteOrderItems, { isLoading: isDeleting }] = useDeleteOrderItemsMutation()

    const [updateOrder] = useUpdateOrderMutation()

    const [allCollapsed, setAllCollapsed] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

    const handleDeleteDialogOpen = () => {
        setIsDeleteDialogOpen(true)
    }

    const handleDeleteDialogClose = () => {
        setIsDeleteDialogOpen(false)
    }

    const collapseAll = () => {
        dispatch(setCollapsedItemsIds(orderItemsIds))
        setAllCollapsed(true)
    }

    const expandAll = () => {
        dispatch(clearCollapsedItems())
        setAllCollapsed(false)
    }

    const handleSelectAllItems = () => {
        setSelectAll(!selectAll)
        if (!selectAll) {
            dispatch(setSelectedItemsIds(orderItemsIds))
        } else {
            dispatch(clearSelectedItems())
        }
    }

    const handleUpdateOrder = async (updatedFields) => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...selectedOrder,
                    ...updatedFields,
                },
            }).unwrap()
        } catch (error) {
            showMutationError(error, t('$t(An error occurred) updating the order.'))
        }
    }

    const handleDeleteItems = async () => {
        const orderItemsCount = selectedItemsArray.length

        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
            handleUpdateOrder({ status: ORDER_STATUS_ARRAY.NotCalculated })
        }

        try {
            await deleteOrderItems({
                organisationId,
                orderId,
                orderItemsIds: selectedItemsArray,
            }).unwrap()
            dispatch(clearSelectedItems())
            dispatch(clearExpandedItems())

            enqueueSnackbar(t(`${orderItemsCount} parts deleted`), { variant: 'success' })
        } catch (error) {
            showMutationError(error, t(`Failed to delete ${orderItemsCount} order items`))
        } finally {
            setIsDeleteDialogOpen(false)
        }
    }

    const handleEditButtonClick = () => {
        setIsEditDialogOpen(true)
    }

    const handleEditDialogClose = () => {
        setIsEditDialogOpen(false)
        dispatch(clearSelectedItems())
    }

    const selectionHasPartsFromLibrary = useMemo(() => {
        return selectedItemsArray.some((id) => {
            const item = orderItems.find((item) => item.id === id)
            return item?.partLibraryEntryId
        })
    }, [orderItems, selectedItemsArray])

    const quoteHasMixedParts = useMemo(() => {
        const selectedParts = orderItems.filter((item) => selectedItemsArray.includes(item.id))
        const hasFlatParts = selectedParts.some((item) => !('profile' in item))
        const hasRotaryParts = selectedParts.some((item) => 'profile' in item)

        return hasFlatParts && hasRotaryParts
    }, [orderItems, selectedItemsArray])

    const itemsSelectedLabel = useMemo(() => {
        const itemsSelectedCount = selectedItemsArray.length
        return orderItemsIds?.length === selectedItemsArray.length && filterOption === 'showAllItems'
            ? 'All items selected'
            : selectedItemsArray.length === 0
              ? 'Select all'
              : `${itemsSelectedCount} ${itemsSelectedCount > 1 ? 'items' : 'item'} selected`
    }, [selectedItemsArray, orderItemsIds])

    useEffect(() => {
        if (!orderItemsIds?.length) {
            if (selectAll !== false) {
                setSelectAll(false)
            }
        } else {
            const allSelected = orderItemsIds.every((id) => selectedItemsArray.includes(id))
            if (selectAll !== allSelected) {
                setSelectAll(allSelected)
            }
        }
    }, [orderItemsIds, selectedItemsArray, selectAll])

    useEffect(() => {
        return () => {
            dispatch(clearSelectedItems())
            dispatch(clearCollapsedItems())
        }
    }, [])

    return (
        <>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 3,
                    justifyContent: 'space-between',
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    <FormControlLabel
                        aria-label="Select all part"
                        control={
                            <Checkbox
                                checked={selectAll}
                                color="secondary"
                                indeterminate={!selectAll ? selectedItemsArray.length > 0 : null}
                                inputProps={{
                                    'data-testid': 'select-all',
                                }}
                                size="small"
                                onChange={handleSelectAllItems}
                            />
                        }
                        data-testid="select-all-label"
                        label={t(itemsSelectedLabel)}
                        sx={{ ml: 0 }}
                    />

                    <TbxTooltip
                        title={
                            !selectedItemsArray.length
                                ? t('Select the parts to edit')
                                : selectionHasPartsFromLibrary
                                  ? t('You cannot edit parts from the library')
                                  : quoteHasMixedParts
                                    ? t(
                                          'To apply a bulk edit, please select only flat parts or only rotary parts, not a mixture of both profile types'
                                      )
                                    : ''
                        }
                        arrow
                    >
                        <span>
                            <Button
                                aria-label="delete"
                                color="primary"
                                disabled={
                                    !selectedItemsArray.length || selectionHasPartsFromLibrary || quoteHasMixedParts
                                }
                                size="small"
                                startIcon={
                                    <HugeiconsIcon
                                        icon={Edit02Icon}
                                        size={16}
                                    />
                                }
                                variant="text"
                                onClick={handleEditButtonClick}
                            >
                                {t('Edit selected items')}
                            </Button>
                        </span>
                    </TbxTooltip>

                    <TbxTooltip
                        title={!selectedItemsArray.length ? t('Select the parts to delete') : ''}
                        arrow
                    >
                        <span>
                            <Button
                                aria-label="delete"
                                color="secondary"
                                disabled={!selectedItemsArray.length}
                                size="small"
                                startIcon={
                                    <HugeiconsIcon
                                        icon={Delete04Icon}
                                        size={16}
                                    />
                                }
                                variant="text"
                                onClick={handleDeleteDialogOpen}
                            >
                                {t('Delete selected items')}
                            </Button>
                        </span>
                    </TbxTooltip>
                </Box>
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    <OrderItemListSort />

                    <OrderItemListFilter />

                    {allCollapsed ? (
                        <IconButton
                            aria-label="Expand all"
                            color="secondary"
                            onClick={expandAll}
                        >
                            <HugeiconsIcon icon={UnfoldMoreIcon} />
                        </IconButton>
                    ) : (
                        <IconButton
                            aria-label="Collapse all"
                            color="secondary"
                            onClick={collapseAll}
                        >
                            <HugeiconsIcon icon={UnfoldLessIcon} />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <TbxDialog
                closeButtonText={t('cancel')}
                confirmButtonText={isDeleting ? t('Deleting...') : t('Proceed')}
                content={t(
                    `You are about to delete ${selectedItemsArray.length} ${
                        selectedItemsArray.length > 1 ? 'parts' : 'part'
                    } from order`
                )}
                handleClose={handleDeleteDialogClose}
                handleConfirmClose={handleDeleteItems}
                isLoading={isDeleting}
                isOpen={isDeleteDialogOpen}
                title={t(`Delete ${selectedItemsArray.length} ${selectedItemsArray.length > 1 ? 'parts' : 'part'}`)}
                uniqueKey={orderId}
            />
            {isEditDialogOpen ? (
                <OrderItemsBulkEdit
                    open={isEditDialogOpen}
                    onClose={handleEditDialogClose}
                />
            ) : null}
        </>
    )
}

OrderItemListActions.propTypes = {
    isLoadingItems: PropTypes.bool,
}

export default OrderItemListActions
